import {ChangeDetectorRef, Component, OnInit, QueryList, signal, ViewChildren} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {FormService} from '../../control/form.service';
import {CommonModule} from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {MatAutocomplete, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatOption} from '@angular/material/core';
import {FormSubmissionResult} from '../../entity/form-submittion-result';
import {ChecklistItem, FormDefinition, LandingPageConfig} from '../../entity/form-definition';
import {FileUploads, UploadingStatus} from '../file-uploads/file-uploads';
import {FileInfo} from '../../entity/form-file';
import {NewSubmission} from '../../entity/new-form-submission';
import {LocalizationPipePipe} from '../../../localization/control/localisation-pipe-pipe';
import {LocalizationService} from '../../../localization/control/localization.service';
import {environment} from '../../../environments/environment';
import {SubmissionFlowState} from '../../entity/submission-flow-state';
import {LandingPageComponent} from '../landing-page/landing-page.component';
import {CompletionPageComponent} from '../completion-page/completion-page.component';

interface Validation {
  type: string;
  value?: any;
  message: string;
}

interface FieldOption {
  label: string;
  value: string;
}

interface Field {
  id: string;
  type: string;
  label: string;
  required: boolean;
  validations?: Validation[];
  options?: FieldOption[];
  sourceId?: string;
}

@Component({
  selector: 'app-dynamic-form',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, FileUploads, LocalizationPipePipe, LandingPageComponent, CompletionPageComponent, MatAutocomplete, MatAutocompleteTrigger, MatFormField, MatLabel, MatInput, MatOption],
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss']
})
export class DynamicFormComponent implements OnInit {
  @ViewChildren(FileUploads) fileUploadComponents!: QueryList<FileUploads>;

  formDefinition?: FormDefinition;
  form?: FormGroup;
  submissionResult?: FormSubmissionResult;
  error?: string;

  submissionFlowState = signal<SubmissionFlowState>('form');
  currentUploadField: string = '';
  uploadProgress: { current: number; total: number } = {current: 0, total: 0};

  logoUrl: string | undefined;
  logoLoadError = false;

  // Completion page data
  completionSubmissionId = '';
  completionFormTitle = '';
  completionSubmittedAt: Date = new Date();

  objectKeys = Object.keys;

  filteredOptions: Record<string, string[]> = {};

  get fileFields(): Field[] {
    return this.formDefinition?.definition.fields.filter((f: Field) => f.type === 'files') || [];
  }

  get nonFileFields(): Field[] {
    return this.formDefinition?.definition.fields.filter((f: Field) => f.type !== 'files') || [];
  }

  get allFileFieldsValid(): boolean {
    if (!this.fileUploadComponents) return true;
    return this.fileUploadComponents.toArray().every(c => c.isValid);
  }

  get canSubmit(): boolean {
    return (this.form?.valid ?? false) && this.allFileFieldsValid;
  }

  get landingPageConfig(): LandingPageConfig | undefined {
    return this.formDefinition?.definition?.landingPage;
  }

  get landingPageChecklistItems(): ChecklistItem[] {
    return this.landingPageConfig?.checklistItems ?? [];
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly formService: FormService,
    private readonly activeRoute: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    private readonly localizationService: LocalizationService
  ) {
  }

  ngOnInit(): void {
    this.reset();
    this.activeRoute.params.subscribe(params => {
      console.log('[DynamicFormComponent] Params:', params);
      if (params['form']) {
        const formId = params['form'];
        this.formService.getFormByName(formId)
          .then(data => {
            console.log('[DynamicFormComponent] Received form definition:', data);
            this.formDefinition = data;
            this.logoLoadError = false;
            this.logoUrl = this.resolveLogoUrl(data);
            this.buildForm();
            this.submissionFlowState.set(this.determineInitialState(data));
            this.cdr.detectChanges();
          })
          .catch(err => {
              this.error = err.message;
              this.formDefinition = undefined;
              this.form = undefined;
              console.error('[DynamicFormComponent] Error fetching form definition:', err);
              this.cdr.detectChanges();
            }
          )
      }
    })
  }

  buildForm(): void {
    console.log('[DynamicFormComponent] Building form from definition.');

    const formGroup: { [key: string]: AbstractControl } = {};

    this.formDefinition?.definition.fields.forEach((field: Field) => {
      if (field.type === 'files') {
        return;
      }

      const validators = [];

      if (field.required) {
        validators.push(Validators.required);
      }

      field.validations?.forEach((validation: Validation) => {
        if (validation.type === 'minLength') {
          validators.push(Validators.minLength(validation.value));
        }
      });

      if (field.id) {
        formGroup[field.id] = this.fb.control('', validators);
      }
    });

    this.form = this.fb.group(formGroup);

    // Initialize filtered options for autocomplete fields
    this.formDefinition?.definition.fields.forEach((field: Field) => {
      if (field.type === 'autocomplete' && field.sourceId) {
        const sourceId = field.sourceId;
        this.filteredOptions[field.id] = this.formDefinition?.definition?.autocompleteSources?.[sourceId] || [];
      }
    });

    console.log('[DynamicFormComponent] Form built successfully.');
  }

  filterAutocomplete(fieldId: string, sourceId: string): void {
    const value = this.form?.get(fieldId)?.value || '';
    const searchTerm = typeof value === 'string' ? value.toLowerCase() : '';
    const allOptions = this.formDefinition?.definition?.autocompleteSources?.[sourceId] || [];
    this.filteredOptions[fieldId] = allOptions.filter(option =>
      option.toLowerCase().includes(searchTerm)
    );
  }

  displayFn(value: string): string {
    return value || '';
  }

  onAutocompleteBlur(fieldId: string, sourceId: string): void {
    const control = this.form?.get(fieldId);
    if (!control) return;
    const value = control.value;
    const allOptions = this.formDefinition?.definition?.autocompleteSources?.[sourceId] || [];
    if (value && !allOptions.includes(value)) {
      control.setValue('');
    }
  }

  async onSubmit(): Promise<void> {
    console.log('[DynamicFormComponent] onSubmit called.');

    if (!this.canSubmit || !this.formDefinition?.formId || !this.formDefinition?.version) {
      console.warn('[DynamicFormComponent] Form is invalid or file validation failed. Submission blocked.');
      return;
    }

    try {
      // Phase 1: Collect file metadata from all file components
      const filesMetadata: Record<string, FileInfo[]> = {};
      const fileComponents = this.fileUploadComponents.toArray();

      for (const component of fileComponents) {
        if (component.selectedFiles.length > 0) {
          filesMetadata[component.fieldName] = component.getFileMetadata();
        }
      }

      // Phase 2: Submit form data with file metadata
      this.submissionFlowState.set('submitting');
      this.cdr.detectChanges();

      console.log('[DynamicFormComponent] Submitting form data with file metadata...');
      const submission: NewSubmission = {
        formId: this.formDefinition.formId,
        formVersion: this.formDefinition.version,
        data: this.form!.value,
        files: filesMetadata
      };

      const result = await this.formService.submitForm(submission);
      this.submissionResult = result;
      console.log('[DynamicFormComponent] Form data submitted. SubmissionId:', result.submissionId);

      // Phase 3: Upload files using returned presigned URLs
      const fieldsWithFiles = fileComponents.filter(c => c.selectedFiles.length > 0);

      if (fieldsWithFiles.length > 0 && result.files) {
        this.submissionFlowState.set('uploading');
        this.uploadProgress = {current: 0, total: fieldsWithFiles.length};
        this.cdr.detectChanges();

        console.log(`[DynamicFormComponent] Uploading files for ${fieldsWithFiles.length} field(s)...`);

        for (const component of fieldsWithFiles) {
          const presignedUrls = result.files[component.fieldName];
          if (!presignedUrls) {
            console.warn(`[DynamicFormComponent] No presigned URLs for field: ${component.fieldName}`);
            continue;
          }

          this.currentUploadField = component.fieldName;
          this.uploadProgress.current++;
          this.cdr.detectChanges();

          console.log(`[DynamicFormComponent] Uploading files for field: ${component.fieldName}`);
          const success = await component.uploadWithPresignedUrls(presignedUrls);

          if (!success) {
            console.error(`[DynamicFormComponent] File upload failed for field: ${component.fieldName}`);
            this.submissionFlowState.set('error');
            this.error = `${this.localizationService.getTranslation('form.error.fileUploadFailed')}: ${component.fieldName}`;
            this.cdr.detectChanges();
            return;
          }
        }
      }

      // Phase 4: Finalize submission
      this.submissionFlowState.set('finalizing');
      this.cdr.detectChanges();
      console.log('[DynamicFormComponent] Finalizing submission:', result.submissionId);

      try {
        await this.formService.finalizeSubmission(result.submissionId);
      } catch (finalizeErr: any) {
        console.error('[DynamicFormComponent] Finalize failed:', finalizeErr);
        this.submissionFlowState.set('error');
        const errMsg = finalizeErr?.message || '';
        if (errMsg.includes('409')) {
          this.error = this.localizationService.getTranslation('form.error.fileVerificationFailed');
        } else {
          this.error = this.localizationService.getTranslation('form.error.finalizeFailed');
        }
        this.cdr.detectChanges();
        return;
      }

      // Complete — store data for completion page
      this.completionSubmissionId = result.submissionId;
      this.completionFormTitle = this.formDefinition.title;
      this.completionSubmittedAt = new Date();
      this.submissionFlowState.set('complete');
      this.form?.reset();
      this.cdr.detectChanges();
      console.log('[DynamicFormComponent] Submission and file upload complete.');

    } catch (err: any) {
      console.error('[DynamicFormComponent] Error during submission:', err);
      this.submissionFlowState.set('error');
      this.error = err?.message || this.localizationService.getTranslation('form.error.submissionFailed');
      this.cdr.detectChanges();
    }
  }

  onStartSubmission(): void {
    this.submissionFlowState.set('form');
  }

  onSubmitAnother(): void {
    this.buildForm();
    this.submissionResult = undefined;
    this.error = undefined;
    this.currentUploadField = '';
    this.uploadProgress = {current: 0, total: 0};
    this.submissionFlowState.set(this.determineInitialState(this.formDefinition));
    this.cdr.detectChanges();
  }

  private determineInitialState(formDef?: FormDefinition): SubmissionFlowState {
    const landingPage = formDef?.definition?.landingPage;
    if (landingPage && landingPage.checklistItems && landingPage.checklistItems.length > 0) {
      return 'landing';
    }
    return 'form';
  }

  private resolveLogoUrl(formDef: FormDefinition): string | undefined {
    if (!formDef.logoUrl) {
      return undefined;
    }
    const baseUrl = environment.fnolPublishedFormsBackend.baseUrl;
    return `${baseUrl}/forms/${formDef.formId}/${formDef.version}/${formDef.logoUrl}`;
  }

  private reset() {
    this.formDefinition = undefined;
    this.form = undefined;
    this.submissionResult = undefined;
    this.error = undefined;
    this.submissionFlowState.set('form');
    this.currentUploadField = '';
    this.uploadProgress = {current: 0, total: 0};
    this.logoUrl = undefined;
    this.logoLoadError = false;
    this.cdr.detectChanges();
  }

  protected onBatchUploadFilesUploading(_: UploadingStatus) {
    this.reset();
  }
}
